import React, { FC, ReactNode, CSSProperties } from "react";
import clsx from "clsx";
import styles from "./typo.module.scss";

type Variant = "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "span" | "body" | "caption";

type Size =
  | "size7"
  | "size8"
  | "size9"
  | "size10"
  | "size11"
  | "size12"
  | "size13"
  | "size14"
  | "size15"
  | "size16"
  | "size17"
  | "size18"
  | "size19"
  | "size20"
  | "size22"
  | "size24"
  | "size30"
  | "size33"
  | "size36";

export type ColorVariant =
  | "checkBackground"
  | "checkGreen"
  | "fontColor"
  | "primaryElectricViolet"
  | "secondaryNeonLime"
  | "tertiaryAquaBlue"
  | "brandPrimary"
  | "brandPrimaryLight"
  | "brandPrimaryDark"
  | "brandSecondary"
  | "white"
  | "neutralLightest"
  | "neutralLighter"
  | "neutralLight"
  | "neutral"
  | "neutralDark"
  | "neutralDarker"
  | "neutralDarkest"
  | "hyperlink"
  | "BorderRed"
  | "NewBorderRed"
  | "titleText"
  | "bodyText1"
  | "bodyText2"
  | "iconColor"
  | "mutedText"
  | "success"
  | "error"
  | "info"
  | "inherit";

type FontWeight = 300 | 400 | 500 | 600 | 700 | 800;

export type TextStyle =
  | "2xsmall-normal"
  | "2xsmall-medium"
  | "xsmall-normal"
  | "xsmall-medium"
  | "xsmall-semibold"
  | "small-normal"
  | "small-medium"
  | "small-semibold"
  | "regular-normal"
  | "regular-medium"
  | "regular-semibold"
  | "regular-bold"
  | "medium-medium"
  | "medium-semibold"
  | "medium-bold"
  | "large-semibold"
  | "large-bold"
  | "xlarge-semibold"
  | "xlarge-bold";

const TEXT_STYLES: Record<TextStyle, { size: Size; weight: FontWeight }> = {
  "2xsmall-normal": { size: "size10", weight: 400 },
  "2xsmall-medium": { size: "size10", weight: 500 },
  "xsmall-normal": { size: "size11", weight: 400 },
  "xsmall-medium": { size: "size11", weight: 500 },
  "xsmall-semibold": { size: "size11", weight: 600 },
  "small-normal": { size: "size12", weight: 400 },
  "small-medium": { size: "size12", weight: 500 },
  "small-semibold": { size: "size12", weight: 600 },
  "regular-normal": { size: "size14", weight: 400 },
  "regular-medium": { size: "size14", weight: 500 },
  "regular-semibold": { size: "size14", weight: 600 },
  "regular-bold": { size: "size14", weight: 700 },
  "medium-medium": { size: "size16", weight: 500 },
  "medium-semibold": { size: "size16", weight: 600 },
  "medium-bold": { size: "size16", weight: 700 },
  "large-semibold": { size: "size18", weight: 600 },
  "large-bold": { size: "size18", weight: 700 },
  "xlarge-semibold": { size: "size20", weight: 600 },
  "xlarge-bold": { size: "size20", weight: 700 },
};

interface TypographyProps {
  variant?: Variant;
  weight?: FontWeight;
  size?: Size;
  textStyle?: TextStyle;
  className?: string;
  children: ReactNode;
  color?: ColorVariant;
  onClick?: (e: React.MouseEvent) => void;
  title?: string;
}

const Typography: FC<TypographyProps> = ({
  variant = "p",
  weight,
  size,
  textStyle,
  className,
  children,
  color = "neutralDarker",
  onClick,
  title,
}) => {
  const Tag = variant as any;
  const preset = textStyle ? TEXT_STYLES[textStyle] : undefined;
  const resolvedSize = size ?? preset?.size;
  const resolvedWeight = weight ?? preset?.weight;
  const style: CSSProperties = {};
  if (resolvedWeight) style.fontWeight = resolvedWeight;

  if (resolvedSize)
    return (
      <Tag className={clsx(styles[variant], styles[resolvedSize], styles[color], className)} style={style} onClick={onClick} title={title}>
        {children}
      </Tag>
    );

  return (
    <Tag className={clsx(styles[variant], styles[color], className)} style={style} onClick={onClick} title={title}>
      {children}
    </Tag>
  );
};

export default Typography;
